import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock } from 'lucide-react';
import { timeAgo } from '../mock/mock';
import { fallbackImage } from '../lib/images';

const onImgError = (a) => (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallbackImage(a.category, a.id);
};

const CAT_LABEL = {
  headlines: 'Headline News', politics: 'Politics', sport: 'Sport', financials: 'Financials', fashion: 'Fashion',
};

function Tag({ children }) {
  return (
    <span className="inline-block text-[10px] font-bold uppercase tracking-wider font-cond text-white bg-[#157347] px-2 py-0.5 rounded">
      {children}
    </span>
  );
}

function Breaking() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider font-cond text-white bg-[#c1121f] px-2 py-0.5 rounded">
      <span className="w-1.5 h-1.5 rounded-full bg-white breaking-dot" /> Breaking
    </span>
  );
}

function Meta({ a, light }) {
  const c = light ? 'text-white/80' : 'text-[#5b6b63]';
  return (
    <div className={`flex items-center gap-3 text-[12px] ${c}`}>
      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{a.city}</span>
      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(a.publishedAt)}</span>
    </div>
  );
}

export default function ArticleCard({ article: a, variant = 'feature' }) {
  if (!a) return null;

  // HERO: large overlay image
  if (variant === 'hero') {
    return (
      <Link to={`/article/${a.id}`} className="group relative block rounded-xl overflow-hidden img-zoom h-[380px] sm:h-[460px] shadow-md">
        <img src={a.image} alt={a.title} onError={onImgError(a)} className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,61,38,0) 30%, rgba(11,61,38,0.85) 100%)' }} />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-2 mb-3">{a.isBreaking ? <Breaking /> : <Tag>{CAT_LABEL[a.category]}</Tag>}</div>
          <h2 className="font-display font-bold text-white text-2xl sm:text-[34px] leading-tight clamp-3 group-hover:underline decoration-2 underline-offset-4">{a.title}</h2>
          <p className="text-white/85 text-sm mt-3 clamp-2 max-w-2xl">{a.excerpt}</p>
          <div className="mt-3"><Meta a={a} light /></div>
        </div>
      </Link>
    );
  }

  // LIST: horizontal thumbnail + text
  if (variant === 'list') {
    return (
      <Link to={`/article/${a.id}`} className="group flex gap-3 py-3">
        <div className="img-zoom rounded-lg overflow-hidden w-24 h-20 sm:w-28 sm:h-24 shrink-0">
          <img src={a.image} alt={a.title} onError={onImgError(a)} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">{a.isBreaking ? <Breaking /> : <Tag>{CAT_LABEL[a.category]}</Tag>}</div>
          <h3 className="font-semibold text-[15px] leading-snug text-[#14241d] clamp-2 headline-link">{a.title}</h3>
          <div className="mt-1.5"><Meta a={a} /></div>
        </div>
      </Link>
    );
  }

  // COMPACT: text only
  if (variant === 'compact') {
    return (
      <Link to={`/article/${a.id}`} className="group block py-2.5">
        <h3 className="font-semibold text-[14px] leading-snug text-[#14241d] clamp-2 headline-link">{a.title}</h3>
        <div className="mt-1"><Meta a={a} /></div>
      </Link>
    );
  }

  // FEATURE (default): vertical card
  return (
    <Link to={`/article/${a.id}`} className="group flex flex-col rounded-xl overflow-hidden bg-white border border-[#e3ece7] hover:shadow-lg transition-shadow duration-300 h-full">
      <div className="img-zoom aspect-[16/10] overflow-hidden">
        <img src={a.image} alt={a.title} onError={onImgError(a)} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">{a.isBreaking ? <Breaking /> : <Tag>{CAT_LABEL[a.category]}</Tag>}</div>
        <h3 className="font-display font-bold text-[19px] leading-snug text-[#14241d] clamp-3 group-hover:text-[#157347] transition-colors">{a.title}</h3>
        <p className="text-sm text-[#5b6b63] mt-2 clamp-2 flex-1">{a.excerpt}</p>
        <div className="mt-3 pt-3 border-t border-[#eef2f0]"><Meta a={a} /></div>
      </div>
    </Link>
  );
}
